
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box, Typography, Button, Paper, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, List, ListItem, ListItemText, Tabs,
  Tab, IconButton, TextField, Grid, Chip, Switch, FormControlLabel,
  Accordion, AccordionSummary, AccordionDetails, Divider, Autocomplete,
  Tooltip,MenuItem,Select,InputLabel,FormControl
} from "@mui/material";
import axios from "axios";
import { ArrowBack } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { fetchUserFromRedis } from "../AuthonticationPages/Register";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";

const uploadDiagnosticApi = process.env.REACT_APP_IMPORT_DIAGNOSTIC_TESTS;
const uploadMedicineApi   = process.env.REACT_APP_IMPORT_MEDICINE_DOCUMENTS;
const getMedicinesApi     = process.env.REACT_APP_GET_MEDICINES;
const updateMedicineApi   = process.env.REACT_APP_GET_MEDICINES;
const validatedistributorApi = process.env.REACT_APP_DISTRIBUTOR_CHECKING_API;
const deleteMedicineApi   = process.env.REACT_APP_GET_MEDICINES;
const getDiagnosticTestsApi  = process.env.REACT_APP_GET_DIAGNOSTIC_TESTS;
const medicineDownload=process.env.REACT_APP_MEDICINE_API_URL;
// ── Helper: highlight batches expiring within 90 days ──────────────────────
const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const exp  = new Date(dateStr);
  const now  = new Date();
  const days = (exp - now) / (1000 * 60 * 60 * 24);
  return days < 90;
};

export default function DiagnosticMasterUpload() {
  const navigate = useNavigate();
  const initialLoadDone  = useRef(false);
  const diagnosticLoaded = useRef(false);
  const medicineLoaded   = useRef(false);

  const [tabValue, setTabValue] = useState(0);
  const [user,     setUser]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const userRole = user?.role || sessionStorage.getItem("userrole");

  const [availableDistributors,  setAvailableDistributors]  = useState([]);
  const [loadingDistributors,    setLoadingDistributors]     = useState(false);
  const [totalMedicines,         setTotalMedicines]          = useState(null);
  const [totalDiagnosticsTests,  setTotalDiagnosticsTests]   = useState(null);
  const [page,       setPage]      = useState(1);
  const [limit]                    = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [testPage,      setTestPage]      = useState(1);
  const [testTotalPages, setTestTotalPages] = useState(1);
  const testLimit = 20;

  const validatedropdownApi = process.env.REACT_APP_DROPDOWN_API;

  // ── User load ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadUser = async () => {
      const userId = sessionStorage.getItem("userId");
      const token  = sessionStorage.getItem("token");
      if (!userId || !token) { setUser(null); setLoading(false); return; }
      try {
        const data = await fetchUserFromRedis(userId, token);
        setUser(data || null);
        if (data?.role) sessionStorage.setItem("userrole", data.role);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const fetchDistributors = useCallback(async () => {
    setLoadingDistributors(true);
    try {
      const res = await axios.get(validatedropdownApi);
      let list = [];
      if (Array.isArray(res.data)) list = res.data;
      else if (res.data.data && Array.isArray(res.data.data)) list = res.data.data;
      else if (res.data.distributors && Array.isArray(res.data.distributors)) list = res.data.distributors;
      setAvailableDistributors(list);
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage("Failed to fetch distributors. Please try again.");
      setDialogOpen(true);
    } finally {
      setLoadingDistributors(false);
    }
  }, []);

  useEffect(() => { fetchDistributors(); }, [fetchDistributors]);

  const showDiagnosticTab = userRole === "admin" || userRole === "diagnosticadmin";
  const showMedicineTab   = userRole === "admin" || userRole === "medicineadmin";

  const currentTabContent = useMemo(() => {
    if (userRole === "diagnosticadmin") return "diagnostic";
    if (userRole === "medicineadmin")   return "medicine";
    if (userRole === "admin") {
      if (showDiagnosticTab && !showMedicineTab) return "diagnostic";
      if (!showDiagnosticTab && showMedicineTab) return "medicine";
      return tabValue === 0 ? "diagnostic" : "medicine";
    }
    return null;
  }, [userRole, tabValue, showDiagnosticTab, showMedicineTab]);

  // ── Diagnostic States ──────────────────────────────────────────────────────
  const [diagnosticFile,          setDiagnosticFile]          = useState(null);
  const [diagnosticUploading,     setDiagnosticUploading]     = useState(false);
  const [diagnosticTests,         setDiagnosticTests]         = useState([]);
  const [filteredDiagnosticTests, setFilteredDiagnosticTests] = useState([]);
  const [diagnosticSearchQuery,   setDiagnosticSearchQuery]   = useState("");
  const [loadingTests,            setLoadingTests]            = useState(false);
  const [editingTest,             setEditingTest]             = useState(null);
  const [editTestData,            setEditTestData]            = useState({});

  // ── Medicine States ────────────────────────────────────────────────────────
  const [medicineFile,        setMedicineFile]        = useState(null);
  const [medicineUploading,   setMedicineUploading]   = useState(false);
  const [medicines,           setMedicines]           = useState([]);
  const [filteredMedicines,   setFilteredMedicines]   = useState([]);
  const [medicineSearchQuery, setMedicineSearchQuery] = useState("");
  const [loadingMedicines,    setLoadingMedicines]    = useState(false);
  const [editingMedicine,     setEditingMedicine]     = useState(null);
  const [editFormData,        setEditFormData]        = useState({});
  const [expandedAccordion,   setExpandedAccordion]   = useState("basic");

  // ── NEW: Batch Dialog State ────────────────────────────────────────────────
  // editingBatch: null = closed, {} = new batch, {batch_id,...} = editing existing
  const [batchDialogOpen,    setBatchDialogOpen]    = useState(false);
  const [editingBatch,       setEditingBatch]       = useState(null);
  const [batchMedicineId,    setBatchMedicineId]    = useState(null); // which medicine
  const [savingBatch,        setSavingBatch]        = useState(false);
  // ──────────────────────────────────────────────────────────────────────────

  // ── Dialog States ──────────────────────────────────────────────────────────
  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [dialogTitle,   setDialogTitle]   = useState("Status");
  const [dialogMessage, setDialogMessage] = useState("");
  const [resultData,    setResultData]    = useState(null);
  const [errors,        setErrors]        = useState([]);

  // ── Delete Dialog ──────────────────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete,     setItemToDelete]     = useState(null);
  const [deleteType,       setDeleteType]       = useState("");
  const [distributorInput, setDistributorInput] = useState("");

  // ── Add Medicine Form States ───────────────────────────────────────────────
  const [showAddMedicineForm, setShowAddMedicineForm] = useState(false);
  const [addMedicineData, setAddMedicineData] = useState({
    name: "", examiner_salt: "", drug_type: "", manufacturer: "",
    price: 0, base_price: 0, revenue_percentage: 0, discounted_price: 0,
    discount_percentage: 0, stock: 0, description: "", category: "",selling_unit:"strip_only",
    image_url: "", prescription_required: false, is_active: true,
    storage: "", reorder: 0, max_qty: 0, hsn_code: "", cgst: 0,
    sgst: 0, igst: 0, pack_size: "", schedule: "", batch_number: "",
    manufacture_date: "", expiry_date: "", selected_distributors: [],
  });
  const [addingMedicine,      setAddingMedicine]      = useState(false);
  const [addMedicineAccordion, setAddMedicineAccordion] = useState("basic");

  // ── Add Diagnostic Test Form States ───────────────────────────────────────
  const [showAddTestForm, setShowAddTestForm] = useState(false);
  const [addTestData,     setAddTestData]     = useState({
    test_code: "", test_name: "", mrp: 0, proposed_rate: 0,
    membership: 0, non_membership: 0, parameters: "",
    category: "", description: "", sample_type: "",
    pretest_preparation: "", report_delivery_time: "",
  });
  const [addingTest, setAddingTest] = useState(false);

  // ── Fetch functions ────────────────────────────────────────────────────────
  const fetchDiagnosticTests = useCallback(async (pageNumber = 1, search = "") => {
    setLoadingTests(true);
    try {
      const res = await axios.get(`${getDiagnosticTestsApi}?page=${pageNumber}&limit=${testLimit}&search=${search}`);
      setDiagnosticTests(res.data.tests || []);
      setFilteredDiagnosticTests(res.data.tests || []);
      setTotalDiagnosticsTests(res.data.total);
      setTestPage(res.data.page);
      setTestTotalPages(res.data.total_pages);
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage("Failed to fetch diagnostic tests. Please try again.");
      setDialogOpen(true);
    } finally {
      setLoadingTests(false);
    }
  }, []);

  const fetchMedicines = useCallback(async (pageNumber = 1, search = "") => {
    setLoadingMedicines(true);
    try {
      const res = await axios.get(`${getMedicinesApi}?page=${pageNumber}&limit=${limit}&search=${search}`);
      const meds = (res.data.medicines || []).map((med) => ({
        ...med,
        distributor_names: Array.isArray(med.distributor_names)
          ? med.distributor_names
          : med.distributor_names ? [med.distributor_names] : [],
      }));
      setMedicines(meds);
      setFilteredMedicines(meds);
      setTotalMedicines(res.data.total);
      setPage(res.data.page);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage("Failed to fetch medicines. Please try again.");
      setDialogOpen(true);
    } finally {
      setLoadingMedicines(false);
    }
  }, []);

  useEffect(() => {
    if (!userRole || initialLoadDone.current) return;
    initialLoadDone.current = true;
    if (currentTabContent === "diagnostic") {
      diagnosticLoaded.current = true;
      fetchDiagnosticTests();
    } else if (currentTabContent === "medicine") {
      medicineLoaded.current = true;
      fetchMedicines();
    }
  }, [userRole]);

  useEffect(() => {
    if (editFormData.distributor_names && Array.isArray(editFormData.distributor_names)) {
      setDistributorInput(editFormData.distributor_names.join(", "));
    } else {
      setDistributorInput("");
    }
  }, [editFormData.distributor_names]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (!userRole) return;
    const newContent = userRole === "admin"
      ? (newValue === 0 ? "diagnostic" : "medicine")
      : currentTabContent;
    if (newContent === "diagnostic" && !diagnosticLoaded.current) {
      diagnosticLoaded.current = true;
      fetchDiagnosticTests();
    } else if (newContent === "medicine" && !medicineLoaded.current) {
      medicineLoaded.current = true;
      fetchMedicines();
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // ── NEW: Batch handlers ────────────────────────────────────────────────────

  // Open dialog for adding a new batch to a given medicine
  const openAddBatchDialog = (medicineId) => {
    setBatchMedicineId(medicineId);
    setEditingBatch({
      batch_number:     "",
      quantity:         0,
      mrp:              0,
      base_price:       0,
      manufacture_date: "",
      expiry_date:      "",
    });
    setBatchDialogOpen(true);
  };

  // Open dialog for editing an existing batch
  const openEditBatchDialog = (medicineId, batch) => {
    setBatchMedicineId(medicineId);
    setEditingBatch({ ...batch });
    setBatchDialogOpen(true);
  };

  // Save (add or update) a single batch via PUT /medicines/:id { batch_update: {...} }
  const handleBatchSave = async () => {
    if (!editingBatch?.batch_number?.trim()) {
      setDialogTitle("Error");
      setDialogMessage("Batch number is required.");
      setDialogOpen(true);
      return;
    }
    if (!editingBatch.mrp || parseFloat(editingBatch.mrp) <= 0) {
      setDialogTitle("Error");
      setDialogMessage("MRP must be greater than 0.");
      setDialogOpen(true);
      return;
    }
    if (!editingBatch.expiry_date) {
      setDialogTitle("Error");
      setDialogMessage("Expiry date is required.");
      setDialogOpen(true);
      return;
    }

    setSavingBatch(true);
    try {
      await axios.put(`${updateMedicineApi}/${batchMedicineId}`, {
        batch_update: {
          batch_number:     editingBatch.batch_number.trim(),
          quantity:         parseInt(editingBatch.quantity) || 0,
          mrp:              parseFloat(editingBatch.mrp),
          base_price:       parseFloat(editingBatch.base_price) || 0,
          manufacture_date: editingBatch.manufacture_date || "",
          expiry_date:      editingBatch.expiry_date,
          is_active:        true,
        },
      });
      setDialogTitle("Success");
      setDialogMessage(
        editingBatch.batch_id
          ? "Batch updated successfully!"
          : "New batch added successfully!"
      );
      setDialogOpen(true);
      setBatchDialogOpen(false);
      setEditingBatch(null);
      setBatchMedicineId(null);
      fetchMedicines(page, medicineSearchQuery);
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage(err.response?.data?.error || "Failed to save batch.");
      setDialogOpen(true);
    } finally {
      setSavingBatch(false);
    }
  };

  // Deactivate a batch via PUT /medicines/:id { deactivate_batch: batchNumber }
  const handleDeactivateBatch = async (medicineId, batchNumber) => {
    try {
      await axios.put(`${updateMedicineApi}/${medicineId}`, {
        deactivate_batch: batchNumber,
      });
      setDialogTitle("Success");
      setDialogMessage(`Batch ${batchNumber} deactivated. Stock recalculated.`);
      setDialogOpen(true);
      fetchMedicines(page, medicineSearchQuery);
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage(err.response?.data?.error || "Failed to deactivate batch.");
      setDialogOpen(true);
    }
  };
  // ──────────────────────────────────────────────────────────────────────────

  // ── Add Medicine handler ───────────────────────────────────────────────────
  const handleAddMedicineFieldChange = (field, value) => {
    setAddMedicineData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "base_price" || field === "revenue_percentage") {
        const bp  = field === "base_price"          ? parseFloat(value) || 0 : prev.base_price;
        const rev = field === "revenue_percentage"  ? parseFloat(value) || 0 : prev.revenue_percentage;
        if (bp > 0) updated.discounted_price = parseFloat((bp * (1 + rev / 100)).toFixed(2));
      }
      return updated;
    });
  };

  const handleAddMedicineSave = async () => {
    if (!addMedicineData.name.trim()) {
      setDialogTitle("Error"); setDialogMessage("Medicine name is required."); setDialogOpen(true); return;
    }
    if (!addMedicineData.price || addMedicineData.price <= 0) {
      setDialogTitle("Error"); setDialogMessage("Price must be greater than 0."); setDialogOpen(true); return;
    }
    const distributorNames = addMedicineData.selected_distributors?.map((d) => d.company_name) || [];
    if (distributorNames.length > 0) {
      try {
        const vr = await axios.post(validatedistributorApi, { distributor_names: distributorNames });
        if (!vr.data.all_valid) {
          setDialogTitle("Warning: Invalid Distributors");
          setDialogMessage(`These distributors are not in the database:\n${vr.data.invalid_distributors.join(", ")}`);
          setDialogOpen(true); return;
        }
      } catch {
        setDialogTitle("Error"); setDialogMessage("Failed to validate distributors."); setDialogOpen(true); return;
      }
    }
    setAddingMedicine(true);
    try {
      const payload = { ...addMedicineData, distributor_names: distributorNames,
        distributor_ids: addMedicineData.selected_distributors?.map((d) => d.distributor_id).filter(Boolean) || [] };
      delete payload.selected_distributors;
      await axios.post(getMedicinesApi, payload);
      setDialogTitle("Success"); setDialogMessage("Medicine added successfully!"); setDialogOpen(true);
      setShowAddMedicineForm(false);
      setAddMedicineData({ name:"", examiner_salt:"", drug_type:"", manufacturer:"",
        price:0, base_price:0, revenue_percentage:0, discounted_price:0,
        discount_percentage:0, stock:0, description:"", category:"",selling_unit:"strip_only",
        image_url:"", prescription_required:false, is_active:true,
        storage:"", reorder:0, max_qty:0, hsn_code:"", cgst:0,
        sgst:0, igst:0, pack_size:"", schedule:"", batch_number:"",
        manufacture_date:"", expiry_date:"", selected_distributors:[] });
      fetchMedicines();
    } catch (err) {
      setDialogTitle("Error"); setDialogMessage(err.response?.data?.error || "Failed to add medicine."); setDialogOpen(true);
    } finally { setAddingMedicine(false); }
  };

  // ── Add Diagnostic Test handler ────────────────────────────────────────────
  const handleAddTestFieldChange = (field, value) =>
    setAddTestData((prev) => ({ ...prev, [field]: value,user_id:user.user_id,email:user.email }));

  const handleRemoveDiagnosticFile = () => {
    setDiagnosticFile(null);
    const fi = document.getElementById("diagnostic-file-upload");
    if (fi) fi.value = "";
  };
  const handleRemoveMedicineFile = () => {
    setMedicineFile(null);
    const fi = document.getElementById("medicine-file-upload");
    if (fi) fi.value = "";
  };
  const downloadMedicinesExcel = async () => {
  try {
    const response = await axios.get(
      `${medicineDownload}/export-excel`,
      {
        responseType: "blob"
      }
    );

    const url = window.URL.createObjectURL(
      new Blob([response.data])
    );

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      "current_medicines.xlsx"
    );

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error("Excel download failed:", error);
  }
};
  const handleAddTestSave = async () => {
    if (!addTestData.test_code.trim()) {
      setDialogTitle("Error"); setDialogMessage("Test code is required."); setDialogOpen(true); return;
    }
    if (!addTestData.test_name.trim()) {
      setDialogTitle("Error"); setDialogMessage("Test name is required."); setDialogOpen(true); return;
    }
    setAddingTest(true);
    try {
      await axios.post(getDiagnosticTestsApi, addTestData);
      setDialogTitle("Success"); setDialogMessage("Diagnostic test added successfully!"); setDialogOpen(true);
      setShowAddTestForm(false);
      setAddTestData({ test_code:"", test_name:"", mrp:0, proposed_rate:0,
        membership:0, non_membership:0, parameters:"", category:"", description:"", sample_type:"" });
      fetchDiagnosticTests();
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage(err.response?.data?.detail || err.response?.data?.error || "Failed to add test.");
      setDialogOpen(true);
    } finally { setAddingTest(false); }
  };

  const handleDiagnosticSearch = (query) => { setDiagnosticSearchQuery(query); fetchDiagnosticTests(1, query); };
  const handleDiagnosticFileChange = (e) => { setDiagnosticFile(e.target.files[0]); setErrors([]); setResultData(null); };

  const handleDiagnosticUpload = async () => {
    if (!diagnosticFile) { setDialogTitle("Error"); setDialogMessage("Please select a file before uploading."); setDialogOpen(true); return; }
    const allowed = ["xlsx","csv","json","txt"];
    const ext = diagnosticFile.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) { setDialogTitle("Invalid File"); setDialogMessage("Allowed: .xlsx, .csv, .json, .txt"); setDialogOpen(true); return; }
    const formData = new FormData();
    formData.append("file", diagnosticFile);
    setDiagnosticUploading(true); setErrors([]); setResultData(null);
    try {
      const res = await axios.post(uploadDiagnosticApi, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setDialogTitle("Success"); setResultData(res.data);
      if (res.data.errors?.length > 0) {
        setErrors(res.data.errors);
        setDialogMessage(`Import completed with warnings!\nInserted: ${res.data.inserted}, Updated: ${res.data.updated}`);
      } else {
        setDialogMessage(`Import Successful!\nInserted: ${res.data.inserted}\nUpdated: ${res.data.updated}\nTotal: ${res.data.total}`);
      }
      setDialogOpen(true); setDiagnosticFile(null); fetchDiagnosticTests();
    } catch (err) { handleUploadError(err); } finally { setDiagnosticUploading(false); }
  };

  const handleEditTestClick = (test) => {
    setEditingTest(test.id);
    setEditTestData({ test_code: test.test_code||"", test_name: test.test_name||"",
      mrp: test.mrp||0, proposed_rate: test.proposed_rate||0,
      membership: test.membership||0, non_membership: test.non_membership||0,
      parameters: test.parameters||"", category: test.category||"",
      description: test.description||"", sample_type: test.sample_type||"",
      pretest_preparation: test.pretest_preparation||"",
      report_delivery_time: test.report_delivery_time||"",
      user_id:user.user_id,email:user.email
      });
      
  };
  const handleEditTestCancel = () => { setEditingTest(null); setEditTestData({}); };
  const handleEditTestFieldChange = (field, value) => setEditTestData((prev) => ({ ...prev, [field]: value }));

  const handleEditTestSave = async (test_id) => {
    try {
      await axios.put(`${getDiagnosticTestsApi}/${test_id}`, editTestData);
      setDialogTitle("Success"); setDialogMessage("Diagnostic test updated successfully!"); setDialogOpen(true);
      setEditingTest(null); setEditTestData({}); fetchDiagnosticTests();
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage(err.response?.data?.detail || "Failed to update diagnostic test.");
      setDialogOpen(true);
    }
  };

  const handleDeleteTestClick  = (test) => { setItemToDelete(test); setDeleteType("test"); setDeleteDialogOpen(true); };
  const handleMedicineFileChange = (e) => { setMedicineFile(e.target.files[0]); setErrors([]); setResultData(null); };

  const handleMedicineUpload = async () => {
    if (!medicineFile) { setDialogTitle("Error"); setDialogMessage("Please select a document file."); setDialogOpen(true); return; }
    const allowed = ["pdf","docx","doc","xlsx","csv"];
    const ext = medicineFile.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) { setDialogTitle("Invalid File"); setDialogMessage("Allowed: .pdf, .docx, .doc, .xlsx, .csv"); setDialogOpen(true); return; }
    const formData = new FormData();
    formData.append("file", medicineFile);
    setMedicineUploading(true); setErrors([]); setResultData(null);
    try {
      const res = await axios.post(uploadMedicineApi, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setDialogTitle("Success"); setResultData(res.data);
      if (res.data.errors?.length > 0) {
        setErrors(res.data.errors);
        setDialogMessage(`Import completed with warnings!\nInserted: ${res.data.inserted}, Updated: ${res.data.updated}`);
      } else {
        setDialogMessage(`Import Successful!\nInserted: ${res.data.inserted}\nUpdated: ${res.data.updated}\nTotal: ${res.data.total}`);
      }
      setDialogOpen(true); setMedicineFile(null); fetchMedicines();
    } catch (err) { handleUploadError(err); } finally { setMedicineUploading(false); }
  };

  const handleMedicineSearch = (query) => { setMedicineSearchQuery(query); fetchMedicines(1, query); };

  const handleEditClick = (medicine) => {
    setEditingMedicine(medicine.medicine_id);
    let selectedDistributors = [];
    if (medicine.distributor_names && Array.isArray(medicine.distributor_names)) {
      selectedDistributors = medicine.distributor_names.map((name) => {
        const match = availableDistributors.find((d) => d.company_name === name || d.name === name);
        return match || { company_name: name, distributor_id: null, name };
      });
    }
    setEditFormData({
      name: medicine.name||"", description: medicine.description||"",
      category: medicine.category||"", manufacturer: medicine.manufacturer||"",
      price: medicine.price||0, discount_percentage: medicine.discount_percentage||0,
      discounted_price: medicine.discounted_price||0, stock: medicine.stock||0,
      image_url: medicine.image_url||"", prescription_required: medicine.prescription_required||false,
      is_active: medicine.is_active !== false,selling_unit:medicine.selling_unit || "strip_only",
      examiner_salt: medicine.examiner_salt||"", drug_type: medicine.drug_type||"",
      storage: medicine.storage||"", reorder: medicine.reorder||0,
      max_qty: medicine.max_qty||0, hsn_code: medicine.hsn_code||"",
      cgst: medicine.cgst||0, sgst: medicine.sgst||0, igst: medicine.igst||0,
      pack_size: medicine.pack_size||"", schedule: medicine.schedule||"",
      selected_distributors: selectedDistributors, distributor_ids: medicine.distributor_ids||[],
      base_price: medicine.base_price||0, revenue_percentage: medicine.revenue_percentage||0,
      batch_number: medicine.batch_number||"",
      manufacture_date: medicine.manufacture_date||"", expiry_date: medicine.expiry_date||"",
    });
    setExpandedAccordion("basic");
  };

  const handleEditCancel = () => {
    setEditingMedicine(null); setEditFormData({});
    setDistributorInput(""); setExpandedAccordion("basic");
  };

  const handleEditFieldChange = (field, value) =>
    setEditFormData((prev) => ({ ...prev, [field]: value }));

  const handleEditSave = async (medicineId) => {
    try {
      const distributorNames = editFormData.selected_distributors?.map((d) => d.company_name) || [];
      const distributorIds   = editFormData.selected_distributors?.map((d) => d.distributor_id).filter(Boolean) || [];
      if (distributorNames.length > 0) {
        try {
          const vr = await axios.post(validatedistributorApi, { distributor_names: distributorNames });
          if (!vr.data.all_valid) {
            setDialogTitle("Warning: Invalid Distributors");
            setDialogMessage(`Not in database:\n${vr.data.invalid_distributors.join(", ")}`);
            setDialogOpen(true); return;
          }
        } catch {
          setDialogTitle("Error"); setDialogMessage("Failed to validate distributors."); setDialogOpen(true); return;
        }
      }
      const updateData = { ...editFormData, distributor_names: distributorNames, distributor_ids: distributorIds };
      delete updateData.selected_distributors;
      const res = await axios.put(`${updateMedicineApi}/${medicineId}`, updateData);
      if (res.status !== 200 || res.data?.success === false)
        throw new Error(res.data?.error || "Failed to update medicine");
      setDialogTitle("Success"); setDialogMessage("Medicine updated successfully!"); setDialogOpen(true);
      setEditingMedicine(null); setEditFormData({});
      await fetchMedicines();
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage(err.response?.data?.error || "Failed to update medicine. Please try again.");
      setDialogOpen(true);
    }
  };

  const handleDeleteClick  = (medicine) => { setItemToDelete(medicine); setDeleteType("medicine"); setDeleteDialogOpen(true); };
  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      if (deleteType === "test") {
        await axios.delete(`${getDiagnosticTestsApi}/${itemToDelete.test_id}`,{params:{user_id:user.user_id,email:user.email}});
        setDialogMessage("Diagnostic test deleted successfully!"); fetchDiagnosticTests();
      } else {
        await axios.delete(`${deleteMedicineApi}/${itemToDelete.medicine_id}`);
        setDialogMessage("Medicine deleted successfully!"); fetchMedicines();
      }
      setDialogTitle("Success"); setDialogOpen(true);
      setDeleteDialogOpen(false); setItemToDelete(null); setDeleteType("");
    } catch (err) {
      setDialogTitle("Error");
      setDialogMessage(err.response?.data?.error || err.response?.data?.detail || "Failed to delete item.");
      setDialogOpen(true);
    }
  };
  const handleDeleteCancel = () => { setDeleteDialogOpen(false); setItemToDelete(null); setDeleteType(""); };

  const handleUploadError = (err) => {
    setDialogTitle("Import Failed");
    let errorMessage = "Import failed. Please check the file format.";
    let errorList = [];
    if (err.response?.data) {
      const data = err.response.data;
      if (data.error) errorMessage = data.error;
      else if (data.message) errorMessage = data.message;
      else if (data.detail) errorMessage = typeof data.detail === "string" ? data.detail : data.detail.message || errorMessage;
      if (data.errors && Array.isArray(data.errors)) errorList = data.errors;
      if (data.details) errorMessage += `\n\n${data.details}`;
    } else if (err.message) errorMessage = `Network Error: ${err.message}`;
    setDialogMessage(errorMessage); setErrors(errorList); setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setErrors([]); setResultData(null); };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: 1, mt: 5, marginLeft:{ xs: 0, md: "300px" }, position: "sticky", top: 0, zIndex: 1000, bgcolor: "#fff" }}>
      <IconButton onClick={() => navigate(-1)} sx={{ color: "#006D77" }}>
        <ArrowBack sx={{ fontSize: 32 }} />
      </IconButton>

      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: "#006D77" }}>
        Master Data Management
      </Typography>

      <Tabs value={tabValue} onChange={handleTabChange}
        TabIndicatorProps={{ sx: { backgroundColor: "#006D77" } }}
        sx={{ fontSize: "1.95rem", "& .MuiTab-root": { color: "#006D77" }, "& .Mui-selected": { color: "#006D77 !important", fontWeight: "bold" } }}>
        {showDiagnosticTab && <Tab sx={{ textTransform: "none", fontSize: "20px", ml: 5 }} label="Diagnostic Tests Management" />}
        {showMedicineTab   && <Tab sx={{ textTransform: "none", fontSize: "20px", ml: 5 }} label="Medicine Management" />}
      </Tabs>

      <Box sx={{ height: "calc(100vh - 160px)", overflowY: "auto" }}>

        {/* ==================== DIAGNOSTIC TAB ==================== */}
        {currentTabContent === "diagnostic" && (
          <>
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, border: "1px solid #ddd", mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Upload Diagnostic Test Data</Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>Upload Excel / CSV / JSON / Text file containing diagnostic test data.</Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="subtitle2" fontWeight={600}>Required Columns:</Typography>
                <Typography variant="body2">• test_code (unique identifier)<br />• test_name</Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Optional Columns:</Typography>
                <Typography variant="body2">• mrp (default: 0)<br />• proposed_rate (default: 0)<br />• category<br />• description<br />• sample_type</Typography>
              </Alert>
              <Box sx={{ mb: 3 }}>
                <input type="file" accept=".xlsx,.csv,.json,.txt" onChange={handleDiagnosticFileChange} style={{ display: "none" }} id="diagnostic-file-upload" />
                <label htmlFor="diagnostic-file-upload">
                  <Button variant="outlined" component="span" sx={{ borderColor: "#006D77", color: "#006D77", textTransform: "none", mb: 2 }}>Choose File</Button>
                </label>
              </Box>
              {diagnosticFile && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>Selected: {diagnosticFile.name}</Typography>
                      <Typography variant="caption">Size: {(diagnosticFile.size / 1024).toFixed(2)} KB</Typography>
                    </Box>
                    <Button variant="outlined" color="error" size="small" onClick={handleRemoveDiagnosticFile}
                      sx={{ textTransform: "none", marginLeft: "800px" }}>Remove</Button>
                  </Box>
                </Alert>
              )}
              <Button variant="contained" onClick={handleDiagnosticUpload} disabled={diagnosticUploading || !diagnosticFile}
                sx={{ backgroundColor: "#006D77", textTransform: "none", px: 4, py: 1.5, "&:hover": { backgroundColor: "#005A63" } }}>
                {diagnosticUploading ? (<><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Uploading...</>) : "Upload File"}
              </Button>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #ddd" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Diagnostic Tests Database ({filteredDiagnosticTests.length} of {totalDiagnosticsTests})
                </Typography>
                <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField size="small" placeholder="Search by ID, code, name, category..."
                    value={diagnosticSearchQuery} onChange={(e) => handleDiagnosticSearch(e.target.value)} sx={{ minWidth: 300 }}
                    InputProps={{ startAdornment: (<Box sx={{ mr: 1, display: "flex", alignItems: "center" }}><Typography sx={{ fontSize: "1.2rem" }}>🔍</Typography></Box>) }} />
                  <Button variant="contained" onClick={() => setShowAddTestForm(true)}
                    sx={{ backgroundColor: "#006D77", textTransform: "none", "&:hover": { backgroundColor: "#005A63" } }}>+ Add Test</Button>
                  <Button variant="outlined" onClick={() => fetchDiagnosticTests()} disabled={loadingTests}
                    sx={{ borderColor: "#006D77", color: "#006D77", textTransform: "none" }}>
                    {loadingTests ? <CircularProgress size={20} /> : "Refresh"}
                  </Button>
                </Box>
              </Box>

              {showAddTestForm && (
                <Paper sx={{ p: 3, mb: 3, border: "2px solid #006D77", borderRadius: 2, backgroundColor: "#f0fafa" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6" color="primary" fontWeight={600}>Add New Diagnostic Test</Typography>
                    <Box>
                      <IconButton size="small" color="success" onClick={handleAddTestSave} disabled={addingTest} sx={{ mr: 1 }}>
                        {addingTest ? <CircularProgress size={20} /> : <SaveIcon />}
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setShowAddTestForm(false)}><CancelIcon /></IconButton>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Test Code *" value={addTestData.test_code} onChange={(e) => handleAddTestFieldChange("test_code", e.target.value)} /></Grid>
                    <Grid item xs={12} md={6}><TextField fullWidth label="Test Name *" value={addTestData.test_name} onChange={(e) => handleAddTestFieldChange("test_name", e.target.value)} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth type="number" label="MRP (₹)" value={addTestData.mrp} onChange={(e) => handleAddTestFieldChange("mrp", parseFloat(e.target.value))} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Proposed Rate (₹)" value={addTestData.proposed_rate} onChange={(e) => handleAddTestFieldChange("proposed_rate", parseFloat(e.target.value))} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Member Price (₹)" value={addTestData.membership} onChange={(e) => handleAddTestFieldChange("membership", parseFloat(e.target.value))} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Non-Member Price (₹)" value={addTestData.non_membership} onChange={(e) => handleAddTestFieldChange("non_membership", parseFloat(e.target.value))} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Category" value={addTestData.category} onChange={(e) => handleAddTestFieldChange("category", e.target.value)} /></Grid>
                    <Grid item xs={12} md={3}><TextField fullWidth label="Sample Type" value={addTestData.sample_type} onChange={(e) => handleAddTestFieldChange("sample_type", e.target.value)} /></Grid>
                    <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Parameters" value={addTestData.parameters} placeholder="e.g., Haemoglobin, WBC, RBC..." onChange={(e) => handleAddTestFieldChange("parameters", e.target.value)} /></Grid>
                    <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Report Delivery Time" placeholder="e.g., 24 hours, Same day"
                      value={addTestData.report_delivery_time}
                      onChange={(e) => handleAddTestFieldChange("report_delivery_time", e.target.value)} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth multiline rows={2} label="Pretest Preparation"
                      placeholder="e.g., Fasting for 8-12 hours required"
                      value={addTestData.pretest_preparation}
                      onChange={(e) => handleAddTestFieldChange("pretest_preparation", e.target.value)} />
                  </Grid>
                    <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={addTestData.description} onChange={(e) => handleAddTestFieldChange("description", e.target.value)} /></Grid>
                  </Grid>
                </Paper>
              )}

              {loadingTests ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
              ) : filteredDiagnosticTests.length === 0 ? (
                <Alert severity="info">{diagnosticSearchQuery ? `No tests found matching "${diagnosticSearchQuery}"` : "No diagnostic tests found."}</Alert>
              ) : (
                <Box>
                  {filteredDiagnosticTests.map((test) => (
                    <Paper key={test.id} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
                      {editingTest === test.id ? (
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" color="primary" fontWeight={600}>Editing: {test.test_name}</Typography>
                            <Box>
                              <IconButton size="small" color="success" onClick={() => handleEditTestSave(test.test_id)} sx={{ mr: 1 }}><SaveIcon /></IconButton>
                              <IconButton size="small" color="error" onClick={handleEditTestCancel}><CancelIcon /></IconButton>
                            </Box>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}><TextField fullWidth label="Test Code *" value={editTestData.test_code} onChange={(e) => handleEditTestFieldChange("test_code", e.target.value)} disabled helperText="Test code cannot be changed" /></Grid>
                            <Grid item xs={12} md={6}><TextField fullWidth label="Test Name *" value={editTestData.test_name} onChange={(e) => handleEditTestFieldChange("test_name", e.target.value)} /></Grid>
                            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="MRP (₹)" value={editTestData.mrp} onChange={(e) => handleEditTestFieldChange("mrp", parseFloat(e.target.value))} /></Grid>
                            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Proposed Rate (₹)" value={editTestData.proposed_rate} onChange={(e) => handleEditTestFieldChange("proposed_rate", parseFloat(e.target.value))} /></Grid>
                            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Member Price (₹)" value={editTestData.membership} onChange={(e) => handleEditTestFieldChange("membership", parseFloat(e.target.value))} /></Grid>
                            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Non-Member Price (₹)" value={editTestData.non_membership} onChange={(e) => handleEditTestFieldChange("non_membership", parseFloat(e.target.value))} /></Grid>
                            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Parameters" value={editTestData.parameters} onChange={(e) => handleEditTestFieldChange("parameters", e.target.value)} placeholder="e.g., Haemoglobin, WBC, RBC..." /></Grid>
                            <Grid item xs={12} md={3}><TextField fullWidth label="Category" value={editTestData.category} onChange={(e) => handleEditTestFieldChange("category", e.target.value)} /></Grid>
                            <Grid item xs={12} md={3}><TextField fullWidth label="Sample Type" value={editTestData.sample_type} onChange={(e) => handleEditTestFieldChange("sample_type", e.target.value)} /></Grid>
                            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={editTestData.description} onChange={(e) => handleEditTestFieldChange("description", e.target.value)} /></Grid>
                            <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Report Delivery Time" placeholder="e.g., 24 hours, Same day"
                              value={editTestData.report_delivery_time}
                              onChange={(e) => handleEditTestFieldChange("report_delivery_time", e.target.value)} />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField fullWidth multiline rows={2} label="Pretest Preparation"
                              placeholder="e.g., Fasting for 8-12 hours required"
                              value={editTestData.pretest_preparation}
                              onChange={(e) => handleEditTestFieldChange("pretest_preparation", e.target.value)} />
                          </Grid>
                          </Grid>
                        </Box>
                      ) : (
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight={600}>{test.test_id}</Typography>
                              <Typography variant="h6" fontWeight={600}>{test.test_name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                Code: {test.test_code}{test.category && ` | ${test.category}`}{test.sample_type && ` | Sample: ${test.sample_type}`}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton size="small" color="primary" onClick={() => handleEditTestClick(test)}><EditIcon /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteTestClick(test)}><DeleteIcon /></IconButton>
                            </Box>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">MRP</Typography><Typography variant="body2" fontWeight={600} color="primary">₹{test.mrp?.toFixed(2)}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Proposed Rate</Typography><Typography variant="body2" fontWeight={600} color="success.main">₹{test.proposed_rate?.toFixed(2)}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Non-Member Price</Typography><Typography variant="body1" fontWeight={700} color="error.main">₹{test.non_membership?.toFixed(2) || "0.00"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">⭐ Member Price</Typography><Typography variant="body1" fontWeight={700} color="success.main">₹{test.membership?.toFixed(2) || "0.00"}</Typography></Grid>
                            {test.parameters && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Parameters</Typography><Typography variant="body2" sx={{ mt: 0.5 }}>{test.parameters}</Typography></Grid>}
                            {test.description && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Description</Typography><Typography variant="body2" sx={{ mt: 0.5 }}>{test.description}</Typography></Grid>}
                            {test.pretest_preparation && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">Pretest Preparation</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>{test.pretest_preparation}</Typography>
                          </Grid>
                        )}
                        {test.report_delivery_time && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary">Report Delivery Time</Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>{test.report_delivery_time}</Typography>
                          </Grid>
                        )}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  ))}
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination count={testTotalPages} page={testPage} onChange={(e, v) => fetchDiagnosticTests(v, diagnosticSearchQuery)} color="primary" />
                  </Box>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ==================== MEDICINE TAB ==================== */}
        {currentTabContent === "medicine" && (
          <>
            {/* Upload Section — unchanged */}
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, border: "1px solid #ddd", mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Upload Medicine Document</Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>Upload Word, Excel, or CSV file containing medicine data.</Typography>
              <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
                <Typography variant="subtitle2" fontWeight={600}>Required Fields:</Typography>
                <Typography variant="body2">• Name, • Price</Typography>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>Optional Fields:</Typography>
                <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                  • Examiner Salt, Drug Type, Manufacturer, Storage, Reorder, Max Qty,
                  HSN Code, CGST, SGST, IGST, Pack Size, Schedule, Category, Description,
                  Stock, Discount %, Prescription Required
                </Typography>
              </Alert>
              <Box sx={{ mb: 3 }}>
                <input type="file" accept=".pdf,.docx,.doc,.xlsx,.csv" onChange={handleMedicineFileChange} style={{ display: "none" }} id="medicine-file-upload" />
                <label htmlFor="medicine-file-upload">
                  <Button variant="outlined" component="span" sx={{ borderColor: "#006D77", color: "#006D77", textTransform: "none", mb: 2 }}>Choose Document</Button>
                </label>
              </Box>
              {medicineFile && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>Selected: {medicineFile.name}</Typography>
                      <Typography variant="caption">Size: {(medicineFile.size / 1024).toFixed(2)} KB</Typography>
                    </Box>
                    <Button variant="outlined" color="error" size="small" onClick={handleRemoveMedicineFile}
                      sx={{ textTransform: "none", marginLeft: "800px" }}>Remove</Button>
                  </Box>
                </Alert>
              )}
              <Button variant="contained" onClick={handleMedicineUpload} disabled={medicineUploading || !medicineFile}
                sx={{ backgroundColor: "#006D77", textTransform: "none", px: 4, py: 1.5, "&:hover": { backgroundColor: "#005A63" } }}>
                {medicineUploading ? (<><CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />Uploading...</>) : "Upload Document"}
              </Button>
                 <Button
                  variant="contained"
                  onClick={downloadMedicinesExcel}
                  sx={{marginLeft:2}}
                >
                  Download Excel
                </Button>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
              <Typography variant="h6" fontWeight={600}>Medicine Database ({filteredMedicines.length} of {totalMedicines})</Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <TextField size="small" placeholder="Search by name, salt, manufacturer..."
                  value={medicineSearchQuery} onChange={(e) => handleMedicineSearch(e.target.value)} sx={{ minWidth: 300 }}
                  InputProps={{ startAdornment: (<Box sx={{ mr: 1, display: "flex", alignItems: "center" }}><Typography sx={{ fontSize: "1.2rem" }}>🔍</Typography></Box>) }} />
                <Button variant="contained" onClick={() => setShowAddMedicineForm(true)}
                  sx={{ backgroundColor: "#006D77", textTransform: "none", "&:hover": { backgroundColor: "#005A63" } }}>+ Add Medicine</Button>
                <Button variant="outlined" onClick={() => fetchMedicines()} disabled={loadingMedicines}
                  sx={{ borderColor: "#006D77", color: "#006D77", textTransform: "none" }}>
                  {loadingMedicines ? <CircularProgress size={20} /> : "Refresh"}
                </Button>
              </Box>
            </Box>

            {/* Add Medicine Form — unchanged */}
            {showAddMedicineForm && (
              <Paper sx={{ p: 3, mb: 3, border: "2px solid #006D77", borderRadius: 2, backgroundColor: "#f0fafa" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" color="primary" fontWeight={600}>Add New Medicine</Typography>
                  <Box>
                    <IconButton size="small" color="success" onClick={handleAddMedicineSave} disabled={addingMedicine} sx={{ mr: 1 }}>
                      {addingMedicine ? <CircularProgress size={20} /> : <SaveIcon />}
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setShowAddMedicineForm(false)}><CancelIcon /></IconButton>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Accordion expanded={addMedicineAccordion === "basic"} onChange={(e, open) => setAddMedicineAccordion(open ? "basic" : false)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Basic Information</Typography></AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Medicine Name *" value={addMedicineData.name} onChange={(e) => handleAddMedicineFieldChange("name", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Examiner Salt / Composition" value={addMedicineData.examiner_salt} onChange={(e) => handleAddMedicineFieldChange("examiner_salt", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Drug Type / Form" value={addMedicineData.drug_type} placeholder="e.g., Tablet, Syrup, Capsule" onChange={(e) => handleAddMedicineFieldChange("drug_type", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Manufacturer" value={addMedicineData.manufacturer} onChange={(e) => handleAddMedicineFieldChange("manufacturer", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Batch Number" value={addMedicineData.batch_number} onChange={(e) => handleAddMedicineFieldChange("batch_number", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Schedule (H, H1, X, etc.)" value={addMedicineData.schedule} onChange={(e) => handleAddMedicineFieldChange("schedule", e.target.value)} /></Grid>
                      <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description / Remarks" value={addMedicineData.description} onChange={(e) => handleAddMedicineFieldChange("description", e.target.value)} /></Grid>
                      
                      <Grid item xs={12}>
                        <Autocomplete multiple options={availableDistributors}
                          getOptionLabel={(o) => typeof o === "string" ? o : o.company_name || o.name || ""}
                          value={addMedicineData.selected_distributors || []}
                          onChange={(e, val) => handleAddMedicineFieldChange("selected_distributors", val)}
                          loading={loadingDistributors}
                          isOptionEqualToValue={(o, v) => o.distributor_id === v.distributor_id || o.company_name === v.company_name}
                          renderInput={(params) => (<TextField {...params} label="Distributors"
                            placeholder={availableDistributors.length === 0 ? "Loading..." : "Select distributors"}
                            helperText={`${availableDistributors.length} distributors available`}
                            InputProps={{ ...params.InputProps, endAdornment: (<>{loadingDistributors ? <CircularProgress size={20} /> : null}{params.InputProps.endAdornment}</>) }} />)}
                          renderTags={(value, getTagProps) => value.map((option, index) => (
                            <Chip key={index} label={option.company_name || option.name || option} {...getTagProps({ index })} color="primary" variant="outlined" />))} />
                      </Grid>
                       <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Selling Unit</InputLabel>
                            <Select
                              value={addMedicineData.selling_unit || "strip_only"}
                              label="Selling Unit"
                              onChange={(e) =>
                                handleAddMedicineFieldChange("selling_unit", e.target.value)
                              }
                            >
                              <MenuItem value="strip_only">Strip Only</MenuItem>
                              <MenuItem value="loose_allowed">Loose Sale Allowed</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                <Accordion expanded={addMedicineAccordion === "pricing"} onChange={(e, open) => setAddMedicineAccordion(open ? "pricing" : false)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Pricing & Tax Information</Typography></AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Price (₹) *" value={addMedicineData.price} onChange={(e) => handleAddMedicineFieldChange("price", parseFloat(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Base Price (₹)" value={addMedicineData.base_price} onChange={(e) => handleAddMedicineFieldChange("base_price", parseFloat(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Revenue % (Alpha)" value={addMedicineData.revenue_percentage} onChange={(e) => handleAddMedicineFieldChange("revenue_percentage", parseFloat(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth disabled label="Calculated Selling Price (₹)" value={addMedicineData.discounted_price ? parseFloat(addMedicineData.discounted_price).toFixed(2) : "0.00"} helperText="Auto-calculated from Base Price + Revenue%" /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Discount %" value={addMedicineData.discount_percentage} onChange={(e) => handleAddMedicineFieldChange("discount_percentage", parseFloat(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth label="HSN Code" value={addMedicineData.hsn_code} onChange={(e) => handleAddMedicineFieldChange("hsn_code", e.target.value)} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="CGST %" value={addMedicineData.cgst} onChange={(e) => handleAddMedicineFieldChange("cgst", parseFloat(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="SGST %" value={addMedicineData.sgst} onChange={(e) => handleAddMedicineFieldChange("sgst", parseFloat(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="IGST %" value={addMedicineData.igst} onChange={(e) => handleAddMedicineFieldChange("igst", parseFloat(e.target.value))} /></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                <Accordion expanded={addMedicineAccordion === "inventory"} onChange={(e, open) => setAddMedicineAccordion(open ? "inventory" : false)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Inventory & Packaging</Typography></AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Stock Quantity" value={addMedicineData.stock} onChange={(e) => handleAddMedicineFieldChange("stock", parseInt(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Reorder Level" value={addMedicineData.reorder} onChange={(e) => handleAddMedicineFieldChange("reorder", parseInt(e.target.value))} /></Grid>
                      <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Max Quantity" value={addMedicineData.max_qty} onChange={(e) => handleAddMedicineFieldChange("max_qty", parseInt(e.target.value))} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Manufacture Date" value={addMedicineData.manufacture_date} placeholder="e.g., 2024-01-01" onChange={(e) => handleAddMedicineFieldChange("manufacture_date", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Expiry Date" value={addMedicineData.expiry_date} placeholder="e.g., 2026-12-31" onChange={(e) => handleAddMedicineFieldChange("expiry_date", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Pack Size" value={addMedicineData.pack_size} placeholder="e.g., 10 Tablets, 100ml" onChange={(e) => handleAddMedicineFieldChange("pack_size", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Storage Conditions" value={addMedicineData.storage} placeholder="e.g., Store below 25°C" onChange={(e) => handleAddMedicineFieldChange("storage", e.target.value)} /></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
                <Accordion expanded={addMedicineAccordion === "settings"} onChange={(e, open) => setAddMedicineAccordion(open ? "settings" : false)}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Additional Settings</Typography></AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}><TextField fullWidth label="Image URL" value={addMedicineData.image_url} onChange={(e) => handleAddMedicineFieldChange("image_url", e.target.value)} /></Grid>
                      <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={addMedicineData.prescription_required} onChange={(e) => handleAddMedicineFieldChange("prescription_required", e.target.checked)} />} label="Prescription Required" /></Grid>
                      <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={addMedicineData.is_active} onChange={(e) => handleAddMedicineFieldChange("is_active", e.target.checked)} />} label="Active Status" /></Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            )}

            {/* Medicine List */}
            <Paper sx={{ p: 3, borderRadius: 2, border: "1px solid #ddd" }}>
              {loadingMedicines ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
              ) : filteredMedicines.length === 0 ? (
                <Alert severity="info">{medicineSearchQuery ? `No medicines found matching "${medicineSearchQuery}"` : "No medicines found in the database."}</Alert>
              ) : (
                <Box>
                  {filteredMedicines.map((medicine) => (
                    <Paper key={medicine.medicine_id} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>

                      {editingMedicine === medicine.medicine_id ? (
                        // ==================== EDIT MODE ====================
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" color="primary" fontWeight={600}>Editing: {medicine.name}</Typography>
                            <Box>
                              <IconButton size="small" color="success" onClick={() => handleEditSave(medicine.medicine_id)} sx={{ mr: 1 }}><SaveIcon /></IconButton>
                              <IconButton size="small" color="error" onClick={handleEditCancel}><CancelIcon /></IconButton>
                            </Box>
                          </Box>
                          <Divider sx={{ mb: 2 }} />

                          {/* Basic Information */}
                          <Accordion expanded={expandedAccordion === "basic"} onChange={handleAccordionChange("basic")}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Basic Information</Typography></AccordionSummary>
                            <AccordionDetails>
                                {/* Note */}
                                <Alert
                                  severity="info"
                                  sx={{ mb: 2, borderRadius: 2 }}
                                >
                                  If multiple batches (2 or more) are available, please edit the
                                  quantity in <strong>Batch Management</strong> instead of updating
                                  stock here.
                                </Alert>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Medicine Name *" value={editFormData.name} onChange={(e) => handleEditFieldChange("name", e.target.value)} /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Examiner Salt / Composition" value={editFormData.examiner_salt} onChange={(e) => handleEditFieldChange("examiner_salt", e.target.value)} /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Drug Type / Form" value={editFormData.drug_type} onChange={(e) => handleEditFieldChange("drug_type", e.target.value)} placeholder="e.g., Tablet, Syrup, Capsule" /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Manufacturer" value={editFormData.manufacturer} onChange={(e) => handleEditFieldChange("manufacturer", e.target.value)} /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Batch Number" value={editFormData.batch_number} onChange={(e) => handleEditFieldChange("batch_number", e.target.value)} placeholder="e.g., BATCH-2024-001" /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Schedule (H, H1, X, etc.)" value={editFormData.schedule} onChange={(e) => handleEditFieldChange("schedule", e.target.value)} /></Grid>
                                <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description / Remarks" value={editFormData.description} onChange={(e) => handleEditFieldChange("description", e.target.value)} /></Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl > 
                                        <InputLabel>Selling Unit</InputLabel>
                                        <Select
                                          value={editFormData.selling_unit || "strip_only"}
                                          label="Selling Unit"
                                          onChange={(e) =>
                                            handleEditFieldChange("selling_unit", e.target.value)
                                          }
                                        >
                                          <MenuItem value="strip_only">Strip Only</MenuItem>
                                          <MenuItem value="loose_allowed">Loose Sale Allowed</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Grid>
                                <Grid item xs={12}>
                                  <Autocomplete multiple options={availableDistributors}
                                    getOptionLabel={(option) => typeof option === "string" ? option : option.company_name || option.name || "Unknown"}
                                    value={editFormData.selected_distributors || []}
                                    onChange={(event, newValue) => handleEditFieldChange("selected_distributors", newValue)}
                                    loading={loadingDistributors}
                                    renderInput={(params) => (<TextField {...params} label="Distributors"
                                      placeholder={availableDistributors.length === 0 ? "Loading distributors..." : "Select distributors"}
                                      helperText={`Select one or more distributors (${availableDistributors.length} available)`}
                                      InputProps={{ ...params.InputProps, endAdornment: (<>{loadingDistributors ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>) }} />)}
                                    renderTags={(value, getTagProps) => value.map((option, index) => (
                                      <Chip label={option.company_name || option.name || option} {...getTagProps({ index })} color="primary" variant="outlined" key={index} />))}
                                    isOptionEqualToValue={(option, value) => {
                                      if (typeof option === "string" && typeof value === "string") return option === value;
                                      return option.distributor_id === value.distributor_id || option.company_name === value.company_name;
                                    }}
                                    filterOptions={(options, state) => {
                                      const iv = state.inputValue.toLowerCase();
                                      return options.filter((o) => (o.company_name||"").toLowerCase().includes(iv) || (o.name||"").toLowerCase().includes(iv));
                                    }} />
                                </Grid>
                                {distributorInput && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary">Preview ({distributorInput.split(",").filter((n) => n.trim()).length} distributors):</Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                      {distributorInput.split(",").map((name, idx) => { const t = name.trim(); if (!t) return null; return <Chip key={idx} label={t} size="small" color="primary" variant="outlined" />; })}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </AccordionDetails>
                          </Accordion>

                          {/* Pricing & Tax */}
                          <Accordion expanded={expandedAccordion === "pricing"} onChange={handleAccordionChange("pricing")}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Pricing & Tax Information</Typography></AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Price (₹) *" value={editFormData.price} onChange={(e) => handleEditFieldChange("price", parseFloat(e.target.value))} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Base Price (₹)" value={editFormData.base_price} onChange={(e) => { const bp = parseFloat(e.target.value); const rev = editFormData.revenue_percentage; const nd = bp > 0 ? parseFloat((bp * (1 + rev / 100)).toFixed(2)) : editFormData.discounted_price; setEditFormData((prev) => ({ ...prev, base_price: bp, discounted_price: nd })); }} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Revenue % (Alpha)" value={editFormData.revenue_percentage} onChange={(e) => { const rev = parseFloat(e.target.value); const bp = editFormData.base_price; const nd = bp > 0 ? parseFloat((bp * (1 + rev / 100)).toFixed(2)) : editFormData.discounted_price; setEditFormData((prev) => ({ ...prev, revenue_percentage: rev, discounted_price: nd })); }} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth disabled label="Calculated Selling Price (₹)" value={editFormData.discounted_price ? parseFloat(editFormData.discounted_price).toFixed(2) : "0.00"} helperText="Auto-calculated from Base Price + Revenue%" /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Discount %" value={editFormData.discount_percentage} onChange={(e) => handleEditFieldChange("discount_percentage", parseFloat(e.target.value))} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth label="HSN Code" value={editFormData.hsn_code} onChange={(e) => handleEditFieldChange("hsn_code", e.target.value)} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="CGST %" value={editFormData.cgst} onChange={(e) => handleEditFieldChange("cgst", parseFloat(e.target.value))} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="SGST %" value={editFormData.sgst} onChange={(e) => handleEditFieldChange("sgst", parseFloat(e.target.value))} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="IGST %" value={editFormData.igst} onChange={(e) => handleEditFieldChange("igst", parseFloat(e.target.value))} /></Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>

                          {/* Inventory & Packaging */}
                          <Accordion expanded={expandedAccordion === "inventory"} onChange={handleAccordionChange("inventory")}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Inventory & Packaging</Typography></AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Stock Quantity" value={editFormData.stock} onChange={(e) => handleEditFieldChange("stock", parseInt(e.target.value))} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Reorder Level" value={editFormData.reorder} onChange={(e) => handleEditFieldChange("reorder", parseInt(e.target.value))} /></Grid>
                                <Grid item xs={12} md={4}><TextField fullWidth type="number" label="Max Quantity" value={editFormData.max_qty} onChange={(e) => handleEditFieldChange("max_qty", parseInt(e.target.value))} /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Manufacture Date" value={editFormData.manufacture_date} onChange={(e) => handleEditFieldChange("manufacture_date", e.target.value)} placeholder="e.g., 2024-01-01" /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Expiry Date" value={editFormData.expiry_date} onChange={(e) => handleEditFieldChange("expiry_date", e.target.value)} placeholder="e.g., 2026-12-31" /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Pack Size" value={editFormData.pack_size} onChange={(e) => handleEditFieldChange("pack_size", e.target.value)} placeholder="e.g., 10 Tablets, 100ml" /></Grid>
                                <Grid item xs={12} md={6}><TextField fullWidth label="Storage Conditions" value={editFormData.storage} onChange={(e) => handleEditFieldChange("storage", e.target.value)} placeholder="e.g., Store below 25°C" /></Grid>
                                 
                              </Grid>
                            </AccordionDetails>
                          </Accordion>

                          {/* Additional Settings */}
                          <Accordion expanded={expandedAccordion === "settings"} onChange={handleAccordionChange("settings")}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={600}>Additional Settings</Typography></AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={2}>
                                <Grid item xs={12}><TextField fullWidth label="Image URL" value={editFormData.image_url} onChange={(e) => handleEditFieldChange("image_url", e.target.value)} /></Grid>
                                <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={editFormData.prescription_required} onChange={(e) => handleEditFieldChange("prescription_required", e.target.checked)} />} label="Prescription Required" /></Grid>
                                <Grid item xs={12} md={6}><FormControlLabel control={<Switch checked={editFormData.is_active} onChange={(e) => handleEditFieldChange("is_active", e.target.checked)} />} label="Active Status" /></Grid>
                              </Grid>
                            </AccordionDetails>
                          </Accordion>

                          {/* ==================== NEW: Batch Management Accordion ==================== */}
                          <Accordion expanded={expandedAccordion === "batches"} onChange={handleAccordionChange("batches")}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", pr: 1 }}>
                                <Typography fontWeight={600}>Batch Management</Typography>
                                <Chip
                                  label={`${(medicine.batches || []).length} batch${(medicine.batches || []).length !== 1 ? "es" : ""}`}
                                  size="small"
                                  sx={{ bgcolor: "#006D77", color: "#fff" }}
                                />
                                <Chip
                                  label={`Total stock: ${medicine.stock}`}
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              {/* Add batch button */}
                              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => openAddBatchDialog(medicine.medicine_id)}
                                  sx={{ backgroundColor: "#006D77", textTransform: "none", "&:hover": { backgroundColor: "#005A63" } }}
                                >
                                  Add Batch
                                </Button>
                              </Box>

                              {/* No batches message */}
                              {(!medicine.batches || medicine.batches.length === 0) ? (
                                <Alert severity="info" sx={{ mb: 1 }}>
                                  No batches recorded. This medicine uses legacy single-stock mode.
                                  Use "Add Batch" to start tracking individual batches.
                                </Alert>
                              ) : (
                                <>
                                  {/* Batch table */}
                                  <Box sx={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                      <thead>
                                        <tr style={{ background: "#f5f5f5" }}>
                                          {["Batch #", "Qty", "MRP (₹)", "Base Price (₹)", "Mfg Date", "Expiry Date", "Status", "Actions"].map((h) => (
                                            <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600,
                                              borderBottom: "2px solid #e0e0e0", whiteSpace: "nowrap" }}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(medicine.batches || []).map((batch, idx) => {
                                          const expiring = isExpiringSoon(batch.expiry_date);
                                          const expired  = batch.expiry_date && new Date(batch.expiry_date) < new Date();
                                          return (
                                            <tr key={batch.batch_id || idx}
                                              style={{ opacity: batch.is_active ? 1 : 0.45,
                                                borderBottom: "1px solid #f0f0f0",
                                                background: expired ? "#fff5f5" : expiring ? "#fffbf0" : "inherit" }}>
                                              <td style={{ padding: "8px 12px", fontWeight: 600 }}>{batch.batch_number}</td>
                                              <td style={{ padding: "8px 12px" }}>
                                                <Chip label={batch.quantity} size="small"
                                                  color={batch.quantity === 0 ? "error" : batch.quantity <= 10 ? "warning" : "default"} />
                                              </td>
                                              <td style={{ padding: "8px 12px" }}>₹{(batch.mrp || 0).toFixed(2)}</td>
                                              <td style={{ padding: "8px 12px" }}>
                                                {batch.base_price ? `₹${batch.base_price.toFixed(2)}` : "-"}
                                              </td>
                                              <td style={{ padding: "8px 12px" }}>{batch.manufacture_date || "-"}</td>
                                              <td style={{ padding: "8px 12px" }}>
                                                <Tooltip title={expired ? "Expired" : expiring ? "Expiring within 90 days" : ""}>
                                                  <span style={{ color: expired ? "#DC2626" : expiring ? "#D97706" : "inherit", fontWeight: (expired || expiring) ? 600 : 400 }}>
                                                    {batch.expiry_date || "-"}
                                                    {expired && " ⚠"}
                                                    {!expired && expiring && " ⏳"}
                                                  </span>
                                                </Tooltip>
                                              </td>
                                              <td style={{ padding: "8px 12px" }}>
                                                <Chip
                                                  label={batch.is_active ? "Active" : "Inactive"}
                                                  size="small"
                                                  color={batch.is_active ? "success" : "default"}
                                                />
                                              </td>
                                              <td style={{ padding: "8px 12px" }}>
                                                <Box sx={{ display: "flex", gap: 0.5 }}>
                                                  <Tooltip title="Edit batch">
                                                    <IconButton size="small" color="primary"
                                                      onClick={() => openEditBatchDialog(medicine.medicine_id, batch)}>
                                                      <EditIcon fontSize="small" />
                                                    </IconButton>
                                                  </Tooltip>
                                                  {batch.is_active && (
                                                    <Tooltip title="Deactivate batch (removes from FEFO pool)">
                                                      <IconButton size="small" color="error"
                                                        onClick={() => handleDeactivateBatch(medicine.medicine_id, batch.batch_number)}>
                                                        <DeleteIcon fontSize="small" />
                                                      </IconButton>
                                                    </Tooltip>
                                                  )}
                                                </Box>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </Box>

                                  {/* Summary row */}
                                  <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f0fafa", borderRadius: 1,
                                    display: "flex", gap: 3, flexWrap: "wrap", border: "1px solid #b2dfdb" }}>
                                    <Typography variant="body2">
                                      <strong>Total stock:</strong> {medicine.stock} units
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Top MRP:</strong> ₹{(medicine.price || 0).toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Active batches:</strong> {(medicine.batches || []).filter((b) => b.is_active).length}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Expiring soon:</strong> {(medicine.batches || []).filter((b) => b.is_active && isExpiringSoon(b.expiry_date)).length}
                                    </Typography>
                                  </Box>
                                </>
                              )}
                            </AccordionDetails>
                          </Accordion>
                          {/* ==================== END Batch Management ==================== */}

                        </Box>
                      ) : (
                        // ==================== VIEW MODE ====================
                        <Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight={600}>{medicine.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {medicine.examiner_salt && `${medicine.examiner_salt} | `}
                                {medicine.drug_type || "N/A"}
                                {medicine.schedule && ` | Schedule: ${medicine.schedule}`}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip label={medicine.is_active ? "Active" : "Inactive"} color={medicine.is_active ? "success" : "default"} size="small" />
                              <IconButton size="small" color="primary" onClick={() => handleEditClick(medicine)}><EditIcon /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteClick(medicine)}><DeleteIcon /></IconButton>
                            </Box>
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Manufacturer</Typography><Typography variant="body2" fontWeight={500}>{medicine.manufacturer || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Batch Number</Typography><Typography variant="body2" fontWeight={500}>{medicine.batch_number || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Drug type</Typography><Typography variant="body2" fontWeight={500}>{medicine.drug_type || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="caption" color="text.secondary">Price</Typography>
                              <Typography variant="body2" fontWeight={600} color="primary">
                                ₹{medicine.price?.toFixed(2)}
                                {medicine.discount_percentage > 0 && (<Chip label={`-${medicine.discount_percentage}%`} size="small" color="error" sx={{ ml: 1, height: 20, fontSize: "0.7rem" }} />)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <Typography variant="caption" color="text.secondary">Stock</Typography>
                              <Typography variant="body2" fontWeight={500}>
                                {medicine.stock || 0}
                                {medicine.reorder > 0 && medicine.stock <= medicine.reorder && (<Chip label="Low" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Base Price</Typography><Typography variant="body2" fontWeight={500}>₹{medicine.base_price?.toFixed(2) || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Revenue %</Typography><Typography variant="body2" fontWeight={500}>{medicine.revenue_percentage || 0}%</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Manufacture Date</Typography><Typography variant="body2" fontWeight={500}>{medicine.manufacture_date || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Expiry Date</Typography><Typography variant="body2" fontWeight={500}>{medicine.expiry_date || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">HSN Code</Typography><Typography variant="body2" fontWeight={500}>{medicine.hsn_code || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Tax (CGST/SGST/IGST)</Typography><Typography variant="body2" fontWeight={500}>{medicine.cgst || 0}% / {medicine.sgst || 0}% / {medicine.igst || 0}%</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Pack Size</Typography><Typography variant="body2" fontWeight={500}>{medicine.pack_size || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Storage</Typography><Typography variant="body2" fontWeight={500}>{medicine.storage || "-"}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Reorder Level</Typography><Typography variant="body2" fontWeight={500}>{medicine.reorder || 0}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Max Quantity</Typography><Typography variant="body2" fontWeight={500}>{medicine.max_qty || 0}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Prescription</Typography><Typography variant="body2" fontWeight={500}>{medicine.prescription_required ? (<Chip label="Required" size="small" color="warning" />) : (<Chip label="Not Required" size="small" color="success" />)}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Discounted Price</Typography><Typography variant="body2" fontWeight={600} color="success.main">₹{medicine.discounted_price?.toFixed(2)}</Typography></Grid>
                            <Grid item xs={6} md={3}><Typography variant="caption" color="text.secondary">Selling Unit</Typography><Typography variant="body2" fontWeight={600} color="success.main">{medicine.selling_unit}</Typography></Grid>

                            {medicine.description && (<Grid item xs={12}><Typography variant="caption" color="text.secondary">Description / Remarks</Typography><Typography variant="body2" sx={{ mt: 0.5 }}>{medicine.description}</Typography></Grid>)}
                            <Grid item xs={12} md={6}>
                              <Typography variant="caption" color="text.secondary">Distributors</Typography>
                              {medicine.distributor_names?.length > 0 ? (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                  {medicine.distributor_names.map((name, idx) => (<Chip key={idx} label={name} size="small" color="primary" variant="outlined" />))}
                                </Box>
                              ) : (<Typography variant="body2" fontWeight={500} color="text.secondary">-</Typography>)}
                            </Grid>

                            {/* NEW: Batch summary chips in view mode */}
                            {medicine.batches && medicine.batches.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                  Batches ({medicine.batches.length} total, {medicine.batches.filter((b) => b.is_active).length} active)
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                  {medicine.batches.map((batch, idx) => {
                                    const expired  = batch.expiry_date && new Date(batch.expiry_date) < new Date();
                                    const expiring = !expired && isExpiringSoon(batch.expiry_date);
                                    return (
                                      <Tooltip
                                        key={batch.batch_id || idx}
                                        title={`MRP: ₹${(batch.mrp || 0).toFixed(2)} | Base: ₹${(batch.base_price || 0).toFixed(2)} | Mfg: ${batch.manufacture_date || "N/A"}`}
                                      >
                                        <Chip
                                          size="small"
                                          variant="outlined"
                                          color={!batch.is_active ? "default" : expired ? "error" : expiring ? "warning" : "primary"}
                                          label={`${batch.batch_number} | qty: ${batch.quantity} | exp: ${batch.expiry_date || "N/A"}`}
                                          sx={{ opacity: batch.is_active ? 1 : 0.5 }}
                                        />
                                      </Tooltip>
                                    );
                                  })}
                                </Box>
                              </Grid>
                            )}
                            {/* END batch chips */}

                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  ))}
                  <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
                    <Pagination count={totalPages} page={page} color="primary" onChange={(event, value) => fetchMedicines(value, medicineSearchQuery)} />
                  </Stack>
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* ==================== RESULT DIALOG ==================== */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle sx={{ backgroundColor: dialogTitle === "Error" || dialogTitle === "Import Failed" ? "#DC2626" : "#006D77", color: "#fff" }}>
            {dialogTitle}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography sx={{ whiteSpace: "pre-line", mb: 2 }}>{dialogMessage}</Typography>
            {resultData && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Inserted:</strong> {resultData.inserted}<br />
                    <strong>Updated:</strong> {resultData.updated}<br />
                    <strong>Total:</strong> {resultData.total}
                  </Typography>
                </Alert>
              </Box>
            )}
            {errors.length > 0 && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={600}>Errors encountered in {errors.length} row(s):</Typography>
                </Alert>
                <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto", p: 2 }}>
                  <List dense>
                    {errors.map((error, idx) => (<ListItem key={idx}><ListItemText primary={error} primaryTypographyProps={{ variant: "body2" }} /></ListItem>))}
                  </List>
                </Paper>
              </Box>
            )}
          </DialogContent>
          <DialogActions><Button onClick={handleCloseDialog} sx={{ color: "#006D77" }}>OK</Button></DialogActions>
        </Dialog>

        {/* ==================== DELETE CONFIRMATION DIALOG ==================== */}
        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle sx={{ backgroundColor: "#DC2626", color: "#fff" }}>Confirm Delete</DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>
              Are you sure you want to delete this {deleteType === "test" ? "diagnostic test" : "medicine"}:{" "}
              <strong>{deleteType === "test" ? itemToDelete?.test_name : itemToDelete?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} sx={{ color: "#666" }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} variant="contained" sx={{ backgroundColor: "#DC2626", "&:hover": { backgroundColor: "#B91C1C" } }}>Delete</Button>
          </DialogActions>
        </Dialog>

        {/* ==================== NEW: ADD / EDIT BATCH DIALOG ==================== */}
        <Dialog open={batchDialogOpen} onClose={() => { setBatchDialogOpen(false); setEditingBatch(null); setBatchMedicineId(null); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ backgroundColor: "#006D77", color: "#fff" }}>
            {editingBatch?.batch_id ? `Edit Batch: ${editingBatch.batch_number}` : "Add New Batch"}
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {editingBatch && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Batch Number *"
                    value={editingBatch.batch_number || ""}
                        sx={{ width: 250 }}

                    disabled={!!editingBatch.batch_id}
                    onChange={(e) => setEditingBatch((prev) => ({ ...prev, batch_number: e.target.value }))}
                    helperText={editingBatch.batch_id ? "Batch number cannot be changed after creation" : "Enter a unique batch identifier"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Quantity *"
                    value={editingBatch.quantity ?? 0}
                    onChange={(e) => setEditingBatch((prev) => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                    inputProps={{ min: 0 }}
                        sx={{ width: 250 }}

                    helperText="Units available in this batch"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="MRP (₹) *"
                    value={editingBatch.mrp ?? 0}
                        sx={{ width: 250 }}

                    onChange={(e) => setEditingBatch((prev) => ({ ...prev, mrp: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Maximum retail price on package"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Base / Purchase Price (₹)"
                    value={editingBatch.base_price ?? 0}
                        sx={{ width: 250 }}

                    onChange={(e) => setEditingBatch((prev) => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ min: 0, step: 0.01 }}
                    helperText="Cost price from distributor"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Manufacture Date"
                    placeholder="e.g., 2024-01"
                    value={editingBatch.manufacture_date || ""}
                        sx={{ width: 250 }}

                    onChange={(e) => setEditingBatch((prev) => ({ ...prev, manufacture_date: e.target.value }))}
                    helperText="Format: YYYY-MM or YYYY-MM-DD"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Expiry Date *"
                    placeholder="e.g., 2026-12"
                    value={editingBatch.expiry_date || ""}
                        sx={{ width: 250 }}

                    onChange={(e) => setEditingBatch((prev) => ({ ...prev, expiry_date: e.target.value }))}
                    helperText="Format: YYYY-MM or YYYY-MM-DD"
                    error={editingBatch.expiry_date && new Date(editingBatch.expiry_date) < new Date()}
                  />
                </Grid>

                {/* Live calculated selling price preview */}
                {editingBatch.mrp > 0 && editingBatch.base_price > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Max discount this batch allows:</strong>{" "}
                        {(((editingBatch.mrp - editingBatch.base_price) / editingBatch.mrp) * 100).toFixed(1)}%
                        (₹{(editingBatch.mrp - editingBatch.base_price).toFixed(2)} per unit)
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        The actual selling price is computed by the AYUJA engine at order time, not here.
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {editingBatch.expiry_date && new Date(editingBatch.expiry_date) < new Date() && (
                  <Grid item xs={12}>
                    <Alert severity="error">This expiry date is in the past. Are you sure?</Alert>
                  </Grid>
                )}
                {editingBatch.expiry_date && isExpiringSoon(editingBatch.expiry_date) && new Date(editingBatch.expiry_date) >= new Date() && (
                  <Grid item xs={12}>
                    <Alert severity="warning">This batch expires within 90 days.</Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setBatchDialogOpen(false); setEditingBatch(null); setBatchMedicineId(null); }} sx={{ color: "#666" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleBatchSave}
              disabled={savingBatch}
              sx={{ backgroundColor: "#006D77", "&:hover": { backgroundColor: "#005A63" } }}
            >
              {savingBatch ? <CircularProgress size={20} color="inherit" /> : editingBatch?.batch_id ? "Update Batch" : "Add Batch"}
            </Button>
          </DialogActions>
        </Dialog>
        {/* ==================== END Batch Dialog ==================== */}

      </Box>
    </Box>
  );
}